"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { reportDesktopExtensionAlive } from "@sukha/query";
import { CHROME_EXTENSION_ID } from "@/constants/chromeExtensionId";
import { sendSessionLifecycleHintToExtension } from "@/lib/utils/chromeExtension";
import { sendToExtension, type ExtensionRuntimeLike } from "@/lib/utils/sendToExtension";
interface ChromeLike {
  runtime?: ExtensionRuntimeLike;
}
interface DetectInstalledResponse {
  installed?: unknown;
  monitoringEnabled?: unknown;
  hasActiveSession?: unknown;
}

/**
 * How often to ping the extension while a session is active. Comfortably below
 * the connection-status heartbeat timeout (5 min) so a single missed ping never
 * lets the desktop badge flip "Off" while the extension is alive.
 */
const LIVENESS_PING_INTERVAL_MS = 45_000;

/**
 * Faster ping cadence right after the session starts, until the extension
 * confirms it is monitoring. Closes the start-of-session window where the
 * badge wrongly told members to connect an extension that was already running.
 */
const STARTUP_BURST_PING_INTERVAL_MS = 5_000;
const STARTUP_BURST_DURATION_MS = 120_000;

/**
 * Min gap between re-sent "session started" lifecycle hints when the extension
 * answers pings as installed but not yet monitoring. The hint makes the
 * extension force-recheck auth and the active session, recovering it from a
 * missed push or a suspended service worker without waiting for its
 * 5-minute fallback poll.
 */
const HINT_RESEND_MIN_INTERVAL_MS = 30_000;

/**
 * Interprets a `detectInstalled` response.
 *
 * Older extension builds only return `{ installed: true }`; for those, any
 * response counts as plain liveness (which the connection-status hook folds in
 * only after a real heartbeat, so it can't manufacture a false "On"). Newer
 * builds report monitoring/session context: monitoring an active session is a
 * strong confirmation that may show "connected" before the first heartbeat
 * row lands; installed-but-not-monitoring means the extension needs a nudge.
 */
function interpretDetectInstalledResponse(response: DetectInstalledResponse): {
  alive: boolean;
  monitoringConfirmed: boolean;
  needsSessionNudge: boolean;
} {
  if (response.installed !== true) {
    return {
      alive: false,
      monitoringConfirmed: false,
      needsSessionNudge: false
    };
  }
  const reportsContext = "hasActiveSession" in response || "monitoringEnabled" in response;
  if (!reportsContext) {
    // Older builds: installed === true is the only available signal.
    return {
      alive: true,
      monitoringConfirmed: false,
      needsSessionNudge: false
    };
  }
  const monitoringOff = response.monitoringEnabled === false;
  const hasActiveSession = response.hasActiveSession === true;
  if (monitoringOff) {
    return {
      alive: false,
      monitoringConfirmed: false,
      needsSessionNudge: false
    };
  }
  if (hasActiveSession) {
    return {
      alive: true,
      monitoringConfirmed: true,
      needsSessionNudge: false
    };
  }
  // Installed and monitoring is on, but it hasn't noticed the session yet.
  return {
    alive: false,
    monitoringConfirmed: false,
    needsSessionNudge: true
  };
}

/**
 * Periodically pings the Chrome extension via `externally_connectable` while a
 * session is active and, on confirmation, reports liveness to the shared
 * connection-status store.
 *
 * This is a transport-independent second freshness source for the desktop
 * (Chrome extension) badge, and the recovery path for an extension that missed
 * the session start: pings run in a fast burst right after the session begins,
 * a monitoring confirmation turns the badge "On" before the first heartbeat
 * row lands, and an installed-but-idle extension gets the "session started"
 * hint re-sent so it force-rechecks the active session.
 *
 * @param enabled - Only ping while this is true (e.g. during an active session).
 */
export function useChromeExtensionLiveness(enabled) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== enabled) {
    t0 = () => {
      if (!enabled || typeof window === "undefined") {
        return;
      }
      const chromeLike = (window as Window & {
        chrome?: ChromeLike;
      }).chrome;
      const runtime = chromeLike?.runtime;
      if (!runtime?.sendMessage) {
        return;
      }
      let isCancelled = false;
      let intervalId = null;
      let monitoringConfirmed = false;
      let lastHintSentAt = 0;
      const enabledAt = Date.now();
      const maybeNudgeExtension = () => {
        const now = Date.now();
        if (now - lastHintSentAt < HINT_RESEND_MIN_INTERVAL_MS) {
          return;
        }
        lastHintSentAt = now;
        sendSessionLifecycleHintToExtension("started");
      };
      const ping = () => {
        sendToExtension(runtime, CHROME_EXTENSION_ID, {
          action: "detectInstalled"
        }).then(reply => {
          if (isCancelled || !reply.ok) {
            return;
          }
          const {
            response
          } = reply;
          if (typeof response !== "object" || response === null) {
            return;
          }
          const verdict = interpretDetectInstalledResponse(response as DetectInstalledResponse);
          if (verdict.alive) {
            reportDesktopExtensionAlive(Date.now(), {
              monitoringConfirmed: verdict.monitoringConfirmed
            });
          }
          if (verdict.monitoringConfirmed && !monitoringConfirmed) {
            monitoringConfirmed = true;
            schedule();
          }
          if (verdict.needsSessionNudge) {
            maybeNudgeExtension();
          }
        });
      };
      const inStartupBurst = () => !monitoringConfirmed && Date.now() - enabledAt < STARTUP_BURST_DURATION_MS;
      const schedule = () => {
        if (isCancelled) {
          return;
        }
        if (intervalId !== null) {
          window.clearInterval(intervalId);
        }
        const cadence = inStartupBurst() ? STARTUP_BURST_PING_INTERVAL_MS : LIVENESS_PING_INTERVAL_MS;
        intervalId = window.setInterval(() => {
          ping();
          if (cadence === STARTUP_BURST_PING_INTERVAL_MS && !inStartupBurst()) {
            schedule();
          }
        }, cadence);
      };
      ping();
      schedule();
      const onWake = () => {
        if (typeof document === "undefined" || document.visibilityState === "visible") {
          ping();
        }
      };
      window.addEventListener("focus", onWake);
      window.addEventListener("online", onWake);
      document.addEventListener("visibilitychange", onWake);
      return () => {
        isCancelled = true;
        if (intervalId !== null) {
          window.clearInterval(intervalId);
        }
        window.removeEventListener("focus", onWake);
        window.removeEventListener("online", onWake);
        document.removeEventListener("visibilitychange", onWake);
      };
    };
    t1 = [enabled];
    $[0] = enabled;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}